import { StyledNewProductForm } from './style';
import { useIntl } from 'react-intl';
import { Select, SelectOption } from '../../Select';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import React, { useEffect, useState } from 'react';
import { DatePicker } from '../../DatePicker';
import { InputNumber } from '../../InputNumber';
import dayjs, { Dayjs } from 'dayjs';
import { MansLauksProduct } from 'ui/Drawer/Drawers/DrawerMansLauksProducts';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';

export interface AvailabilityOptions {
  value: number;
  label: string;
}

export interface unitTypes {
  id: number;
  name: string;
  short: string;
  type: string;
  status: string;
  extension: boolean;
}

interface NewProductRetailProps {
  mansLauksSelectedProduct?: MansLauksProduct;
}

export const NewProductRetail = ({
  mansLauksSelectedProduct,
}: NewProductRetailProps) => {
  const [selectedUnit, setSelectedUnit] = useState('');
  const form = useFormInstance();

  const intl = useIntl();

  const { data } = useQueryApiClient({
    request: {
      url: 'api/v2/products/form/attributes',
    },
  });

  useEffect(() => {
    const storageUnitValue = form.getFieldValue('storage_unit');
    if (storageUnitValue) {
      setSelectedUnit(storageUnitValue);
    }
  }, []);

  const UnitSuffix = () => {
    return selectedUnit ? (
      <div className={'unit-suffix'}>
        <div>Є/{selectedUnit}</div>
      </div>
    ) : (
      <></>
    );
  };

  const MalaSuffix = () => {
    return mansLauksSelectedProduct ? (
      <div className={'unit-suffix mala'}>
        <div>{`${intl.formatMessage({ id: 'products.mans_lauks_amount' })} ${
          mansLauksSelectedProduct.amount
        }`}</div>
      </div>
    ) : (
      <></>
    );
  };

  const disabledDate = (current: Dayjs) => {
    return current && current < dayjs().startOf('day');
  };
  const isPackage = selectedUnit !== 'iep';
  const unitTypes: unitTypes[] = data && data['storageUnits'];
  const extensionUnitTypes =
    unitTypes &&
    unitTypes.filter((value) => {
      return value.extension === true;
    });

  const availabilityOptions: AvailabilityOptions[] = [
    {
      label: intl.formatMessage({ id: 'availability_options.seasonal' }),
      value: 1,
    },
    {
      label: intl.formatMessage({ id: 'availability_options.on_order' }),
      value: 2,
    },
    {
      label: intl.formatMessage({ id: 'availability_options.all_year' }),
      value: 3,
    },
  ];

  return (
    <StyledNewProductForm>
      <div className={'unit-step col-djfgr'}>
        <div className={'unit'}>
          <Select
            showSearch={false}
            className={'scnd-tr'}
            name={['retail', 'storage_unit']}
            label={intl.formatMessage({ id: 'general.unit' })}
            onChange={(value: string) => setSelectedUnit(value)}
            validations={'required'}
            children={
              unitTypes &&
              unitTypes.map((unit, index) => {
                return (
                  <SelectOption value={unit.short} key={`${index} - opt`}>
                    {unit.name} {unit.short.length > 0 ? `(${unit.short})` : ''}
                  </SelectOption>
                );
              })
            }
          />
        </div>
        <div className={'step'}>
          <InputNumber
            className={'scnd-tr'}
            name={['retail', 'step']}
            label={intl.formatMessage({ id: 'general.step' })}
            validations={['required', 'correctStepForUnit']}
            controls={false}
          />
        </div>
      </div>

      {!isPackage && (
        <div className={'amount-merv col-djfgr'}>
          <div className={'amount'}>
            <InputNumber
              validations={['required']}
              className={'scnd-tr'}
              disabled={isPackage}
              name={['retail', 'package_unit_val']}
              label={intl.formatMessage({ id: 'general.amount' })}
              controls={false}
            />
          </div>
          <div className={'merv'}>
            <Select
              validations={'required'}
              className={'scnd-tr'}
              disabled={isPackage}
              name={['retail', 'package_unit']}
              label={intl.formatMessage({ id: 'general.unit' })}
              children={
                extensionUnitTypes &&
                extensionUnitTypes.map((unit, index) => {
                  return (
                    <SelectOption value={unit.short} key={`${index} - opt`}>
                      {unit.name}
                      {unit.short.length > 0 ? `(${unit.short})` : ''}
                    </SelectOption>
                  );
                })
              }
            />
          </div>
        </div>
      )}

      <div className={'minamount-maxamount col-djfgr'}>
        <div className={'available-amount'}>
          <InputNumber
            suffix={<MalaSuffix />}
            className={'flex'}
            validations={['required', 'minZeroNumber']}
            controls={false}
            name={['retail', 'storage_stock_count']}
            label={intl.formatMessage({ id: 'general.availability_amount' })}
          />
        </div>
        <div className={'minamount'}>
          <InputNumber
            className={'flex'}
            controls={false}
            name={['retail', 'storage_limit_min']}
            label={intl.formatMessage({
              id: 'general.min_amount_one_purchase',
            })}
            onChange={() => {
              form.validateFields(['storage_limit_max']);
            }}
            validations={'minLimitNotGreaterThanMax'}
          />
        </div>
        <div className={'maxamount'}>
          <InputNumber
            className={'flex'}
            controls={false}
            onChange={() => {
              form.validateFields(['storage_limit_min']);
            }}
            name={['retail', 'storage_limit_max']}
            label={intl.formatMessage({ id: 'general.max_amount' })}
            validations={'minLimitNotGreaterThanMax'}
          />
        </div>
      </div>

      <div className={'price-discount-time col-djfgr'}>
        <div className={'price'}>
          <InputNumber
            className={'flex'}
            suffix={<UnitSuffix />}
            validations={[
              'required',
              'minZeroNumber',
              'priceGreaterThanDiscount',
            ]}
            name={['retail', 'storage_stock_price']}
            label={intl.formatMessage({ id: 'product.price_vat' })}
            controls={false}
          />
        </div>
        <div className={'discount'}>
          <InputNumber
            className={'flex'}
            suffix={<UnitSuffix />}
            name={['retail', 'storage_stock_discount_price']}
            label={intl.formatMessage({ id: 'product.discount_price_vat' })}
            controls={false}
          />
        </div>
        <div className={'time'}>
          <DatePicker
            className={'flex'}
            name={['retail', 'discount_expiration_date']}
            label={intl.formatMessage({ id: 'general.discount_time' })}
            disabledDate={disabledDate}
            disabled={true} // Jauns ips "Jauna funkcionalitāte"
          />
        </div>
      </div>

      <div className={'availability'}>
        <Select
          validations={'required'}
          name={['retail', 'availability']}
          label={intl.formatMessage({ id: 'general.availability' })}
          size={'middle'}
          children={availabilityOptions.map((options) => {
            return (
              <SelectOption value={options.value} key={options.value}>
                {options.label}
              </SelectOption>
            );
          })}
        />
      </div>

      <div className={'prod-availability'}>
        <div className={'title'}>
          {intl.formatMessage({ id: 'general.product_viable_in_mass' })}
        </div>
        <div className={'min-price col-djfgr'}>
          <div className={'min'}>
            <div className={'label'}>
              {intl.formatMessage({
                id: 'general.min_amount',
              })}
            </div>
            <InputNumber
              className={'scnd-tr'}
              name={['retail', 'storage_wholesale_limit']}
            />
          </div>
          <div className={'price'}>
            <div className={'label'}>
              {intl.formatMessage({ id: 'general.bulk_price' })}
            </div>
            <InputNumber
              className={'scnd-tr'}
              name={['retail', 'storage_wholesale_price']}
            />
          </div>
        </div>
      </div>
    </StyledNewProductForm>
  );
};
